import React, {useMemo, useState} from 'react';
import Translate, {t} from "../../../../../../../components/Translate";
import BarsChart from "../../../../../../../components-new/general/charts/BarsChart";
import useChartPalette from "../../../../../../../components-new/general/charts/palette";
import TranslateProperty from "../../../../../../../components/TranslateProperty";
import {
    filterInputStyle,
    formatSecondsToHHMMSS,
    getTodayDate,
    getTomorrowDate,
    globalExportToExcel
} from "../../../../../../../utils/General";
import config from "../../../../../../../config";
import ExcelButton from "../../../../../../../components-new/buttons/ExcelButton";
import Input from "../../../../../../../components-new/form/Input";
import ResetButton from "../../../../../../../components-new/buttons/ResetButton";
import DatePicker from "../../../../../../../components-new/form/DatePicker";
import Checkbox from "../../../../../../../components-new/form/Checkbox";
import DropdownSelect from "../../../../../../../components-new/form/DropdownSelect";
import SearchInput from "../../../../../../../components-new/form/SearchInput";
import ColorIndicator from "../../../../../../../components-new/general/ColorIndicator";

var currentStatusMap = config.currentStatusMap

/* the order a shift passes through them, which is also the order they stack */
const STATUS_KEYS = [
    currentStatusMap.available,
    currentStatusMap.pause,
    currentStatusMap.notHere,
    currentStatusMap.offline,
];

/*
 * A duration, short enough for an axis tick.
 *
 * Ticks are written every eighty pixels or so, and "02:30:00" at that spacing
 * runs into its neighbour. The exact seconds are still in the tooltip and in
 * the exported sheet.
 */
const formatDuration = (seconds) => {
    const total = Math.round(seconds || 0);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);

    if (hours > 0) return hours + 'h ' + minutes + 'm';
    if (minutes > 0) return minutes + 'm';
    return total + 's';
};
const OperatorWorkingHours = ({operatorWorkTimeData, user, whStartDate, whEndDate, setWhStartDate, setWhEndDate}) => {
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [topOnly, setTopOnly] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const statusOptions = [
        {labelKey: 'allStatuses', value: 'ALL'},
        {labelKey: 'available', value: currentStatusMap.available},
        {labelKey: 'pause', value: currentStatusMap.pause},
        {labelKey: 'notHere', value: currentStatusMap.notHere},
        {labelKey: 'offline', value: currentStatusMap.offline},
    ];

    const handleExport = () => {
        const columns = [
            {label: 'Operator', value: 'operator'},
            {label: 'Available', value: row => formatSecondsToHHMMSS(row[currentStatusMap.available])},
            {label: 'Pause', value: row => formatSecondsToHHMMSS(row[currentStatusMap.pause])},
            {label: 'Not here', value: row => formatSecondsToHHMMSS(row[currentStatusMap.notHere])},
            {label: 'Offline', value: row => formatSecondsToHHMMSS(row[currentStatusMap.offline])},
        ];

        globalExportToExcel({
            data: filteredData,
            columns,
            fileName: 'Operators working hours (' + whStartDate + ' ' + whEndDate + ').xlsx',
        });
    };

    const resetFilters = () => {
        setStatusFilter('ALL');
        setSearchTerm('');
        setTopOnly(true);
        setWhStartDate(getTodayDate());
        setWhEndDate(getTomorrowDate());
    }

    const filteredData = useMemo(() => {
        let data = [...operatorWorkTimeData];

        if (statusFilter !== "ALL") {
            data = data.filter(op => op[statusFilter] > 0);
        }

        if (searchTerm.trim()) {
            data = data.filter(op =>
                op.operator.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (topOnly) {
            data = data
                .filter(op => op[currentStatusMap.available] > 0)
                .sort((a, b) => (b[currentStatusMap.available] || 0) - (a[currentStatusMap.available] || 0))
                .slice(0, 10);
        }

        return data;
    }, [operatorWorkTimeData, statusFilter, topOnly, searchTerm]);

    const palette = useChartPalette();

    /*
     * The statuses as segments of one bar, in the order a shift passes through
     * them.
     *
     * Four bars and a name per operator meant ten operators filled the width,
     * and the names were written along the bottom where anything longer than
     * "Anna K" was cut or turned on its side. One bar per operator with the
     * names down the left shows the whole shift and its split in the same
     * length, and the names are simply readable.
     */
    const statusSeries = useMemo(() => {
        const colors = {};
        colors[currentStatusMap.available] = palette.statusAvailable;
        colors[currentStatusMap.pause] = palette.statusPause;
        colors[currentStatusMap.notHere] = palette.statusNotHere;
        colors[currentStatusMap.offline] = palette.statusOffline;

        const all = STATUS_KEYS.map(key => ({key: key, color: colors[key]}));

        return all
            .filter(item => statusFilter === 'ALL' || statusFilter === item.key)
            .map(item => ({
                key: item.key,
                name: t({text: item.key, user}),
                color: item.color,
            }));
    }, [statusFilter, palette, user]);

    /*
     * Every status spelled out, even the ones that never happened.
     *
     * The server only writes the statuses an operator actually went through, so
     * an operator who was available all day comes back as {operator, AVAILABLE}
     * with no key for the other three. Four separate bars did not mind - a
     * missing value simply drew nothing - but a stack is built by adding each
     * segment to the one below it, and a missing value there is not a zero, it
     * breaks the sum and the rest of the stack never appears. Which is exactly
     * what one bar per operator looked like.
     *
     * BarsChart reads the category off `name`; the server calls it `operator`.
     */
    const chartRows = useMemo(
        () => filteredData.map(row => {
            const filled = {name: row.operator};

            STATUS_KEYS.forEach(key => {
                filled[key] = row[key] || 0;
            });

            return filled;
        }),
        [filteredData]
    );

    return (
        <div>
            <div style={{marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'baseline'}}>
                <DropdownSelect
                    selectedValue={statusOptions.find(o => o.value === statusFilter)}
                    onChange={(s) => setStatusFilter(s.value)}
                    options={statusOptions}
                    renderIndicator={option => option && option.value !== 'ALL'
                        ? <ColorIndicator currentStatus={option.value}/>
                        : null}
                    renderLabel={s => t({text: s.labelKey, user})}
                    variant={'outline'}
                    styles={{
                        width: '300px',
                    }}
                >
                    {option => (
                        <span style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                            {option.value !== 'ALL' && <ColorIndicator currentStatus={option.value}/>}
                            <span>{t({text: option.labelKey, user})}</span>
                        </span>
                    )}
                </DropdownSelect>

                <Checkbox
                    checked={topOnly}
                    handleChange={(e) => setTopOnly(e)}
                    label={t({text: 'top10AvailableOperators', user})}
                />

            </div>
            <div style={{
                display: 'flex',
                gap: '20px',
                marginBottom: '20px',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}>
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={'searchByOperator'}
                        style={{width: '300px', height: '40px'}}
                    />

                    <DatePicker
                        inputContainerStyle={{width: '240px', height: '40px'}}
                        value={whStartDate}
                        onChange={e => setWhStartDate(e)}
                        onReset={() => setWhStartDate(getTodayDate())}
                        defaultValue={getTodayDate()}
                        panelAlign={{horizontal: 'right', vertical: ''}}
                    />

                    <DatePicker
                        inputContainerStyle={{width: '240px', height: '40px'}}
                        value={whEndDate}
                        onChange={e => setWhEndDate(e)}
                        onReset={() => setWhEndDate(getTomorrowDate())}
                        defaultValue={getTomorrowDate()}
                    />

                    {(searchTerm !== '' || whStartDate.getTime() !== getTodayDate().getTime() || whEndDate.getTime() !== getTomorrowDate().getTime() || statusFilter !== 'ALL' || !topOnly)
                        &&
                        <ResetButton
                            onClick={resetFilters}
                            width={150}
                            height={40}
                            variant="outline"
                        />
                    }

                </div>

                <ExcelButton
                    onClick={handleExport}
                    width={190}
                    height={40}
                    variant="outline"
                    children={
                        <span style={{fontSize: '12px', backgroundColor: 'transparent'}}>
                            <Translate text={'downloadXls'}/>
                        </span>
                    }
                />
            </div>

            <div className="chart-row" style={{marginBottom: 0}}>
                <BarsChart
                    data={chartRows}
                    series={statusSeries}
                    layout="vertical"
                    categoryWidth={170}
                    /* ten rows at a thickness worth reading, plus the axis */
                    height={440}
                    valueFormatter={formatDuration}
                />

                <div className="chart-legend" style={{width: '100%'}}>
                    {statusSeries.map(item => (
                        <span className="chart-legend__item" key={item.key}>
                            <span className="chart-legend__swatch" style={{backgroundColor: item.color}}/>
                            <span className="chart-legend__name">{item.name}</span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default OperatorWorkingHours;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/operators/OperatorWorkingHours.jsx